import logging from '@sstdev/lib_logging';
import { setDefaultValuesIfNecessary } from 'lib_ui-core';
import { metadata, rfidStrategy } from 'lib_ui-services';
import lodash from 'lodash';
const { cloneDeep } = lodash;

const _p = { setDefaultValuesIfNecessary };
export const _private = _p;

/**
 * Extract the field changes configured on a Transaction form's newRecord, for use as continuous
 * scan's formMetadata. Mirrors the destructuring in willCreate_namespace_relation_transaction.js,
 * which strips these same keys server-side when splitting the transaction record's own fields
 * from the <relation> field changes for the existing one-tag-at-a-time submit.
 * @param {object} [newRecord]
 * @returns {object | null}
 */
export function getFormMetadataFromNewRecord(newRecord) {
    if (newRecord == null) return null;
    // eslint-disable-next-line no-unused-vars
    const { $loki, entries, meta, title, _id, ...changes } = newRecord;
    return changes;
}

/**
 * The same change-set as getFormMetadataFromNewRecord, but measured after the record has been
 * brought up to date the way the save pipeline brings it up to date.
 *
 * A Mass Update field restored by `DefaultToPreviousSelection` (the 3-tier Company/Building/Room
 * cascade, and any other field whose metadata carries a DTPS node) is restored straight into
 * component state -- `useDefaultValueForHNode` -> `useFormControl`'s value fallback -- and is never
 * written to the form's `newRecord`. Only user interaction writes that record. Every save path
 * compensates by running `setDefaultValuesIfNecessary` over a clone before deriving `changes`
 * (willCreate_namespace_relation.js, doingValidate_namespace_relation.js, and the empty-change-set
 * guard in doingValidate_namespace_relation_transaction.js -- see GH #1521).
 *
 * Continuous scan is a second way to finish the same form -- an RFID trigger pull, RFID button tap
 * or focusing the scan input hands off to the Active Count route directly -- and it reaches none of
 * those rules. Reading `newRecord` raw there makes a restored value invisible twice over: the
 * hand-off refuses to start ("Select a value to update before scanning tags.") on a form that
 * visibly has values, and any restored field that isn't the one the user just touched is silently
 * dropped from SessionStart's formMetadata, so the server applies less than the screen showed.
 *
 * @param {object} [formContext] - the value from `useContext(contexts.FormContext)`
 * @returns {Promise<object | null>}
 */
export async function getStagedFormMetadata(formContext) {
    const { newRecord, state } = formContext ?? {};
    if (newRecord == null) return null;
    const { hNode, namespace, relation, isNew } = state?.context ?? {};
    const staged = cloneDeep(newRecord);
    try {
        // Matches the rules' own call shape. Transaction hardcodes forAction 'new', so isNew is
        // true here in practice; default it rather than skip the pass, since the DTPS restoration
        // this exists for only runs on the isNew branch.
        await _p.setDefaultValuesIfNecessary({ hNode, namespace, relation, isNew: isNew ?? true }, staged);
    } catch (err) {
        // Never let a metadata/storage hiccup block scanning - fall back to the raw record, which
        // is exactly the pre-existing behaviour.
        logging.warn('[ContinuousScan] Failed to stage restored form defaults:', err);
        return getFormMetadataFromNewRecord(newRecord);
    }
    return getFormMetadataFromNewRecord(staged);
}

/**
 * #1690 moves tagPrefix from deviceManagement:settings to identity:tenant.settings.rfid.
 * Read it through tenantSetting so continuous scans use the migrated tenant setting.
 * @see https://github.com/sstid/backbone/issues/1690
 * Resolve the tenant's tag prefix (identity:tenant.settings.rfid.tagPrefix) for a continuous
 * scan SessionStart, or undefined if none applies. Filters RFID reads at the SDK level (see
 * ZebraRfid.applyContinuousScanTagPrefixFilter), so the reader itself never reports non-matching
 * tags. Only applies to proprietary-tag tenants - GS1 tenants have no tagPrefix concept, so skip
 * the fetch entirely for them (the round trip would be pure waste). Shared by MarkRecord and
 * MultiPickerTransactionGrid's SessionStart paths so this logic only needs fixing once.
 * @param {Function} request - the request function from useEventSink()
 * @returns {Promise<string | undefined>}
 */
export async function getTagPrefixForSession(request) {
    const { useGs1 } = rfidStrategy.computeZeroTrimPolicyFromConfig();
    if (useGs1) return undefined;
    try {
        const settingsResult = await request({}, { verb: 'get', namespace: 'identity', relation: 'tenantSetting', type: 'find' });
        return settingsResult?.result?.[0]?.rfid?.tagPrefix || undefined;
    } catch (err) {
        logging.warn('[ContinuousScan] Failed to fetch tag prefix setting:', err);
        return undefined;
    }
}

/**
 * Resolve the active tenant use case from SessionContext, for a continuous scan session's
 * useCaseId/useCaseTitle. Falls back to Mass Update only when no use case is available -
 * unlike Physical Inventory Count (MarkRecord), Mass Update has no meaningful "no use case
 * selected" default, so falling back to physical-inventory there would send a streamType/
 * useCaseId pair the server-side authorization check treats as inconsistent. Shared by
 * MultiPickerTransactionGrid's index.js/index.native.js so the two copies can't diverge.
 * @param {*} sessionContext
 * @returns {{ _id: string, title: string }}
 */
export function resolveUseCaseForContinuousScan(sessionContext) {
    const useCase = sessionContext?.useCase?.['metaui:useCase'] ?? sessionContext?.session?.useCase?.['metaui:useCase'];
    return useCase ?? { _id: 'mass-update', title: 'Mass Update' };
}

/**
 * Resolve the active tenant's title for a continuous scan session, following the same fallback
 * chain the two call sites already use for `tenantId` so the id and the title always come from the
 * same tenant object.
 *
 * This is not a display string. silo/stream stores it as the session's `tenantTitle`, and
 * emitSocket routes the desktop's live update to the Socket.IO room `<tenantTitle>.<useCaseTitle>`
 * — the room the web client joined via `selectTenant` using `activeTenant.title`. The Android
 * client used to hardcode it to the literal "Tenant", so the emit went to a room nobody was in and
 * the desktop only picked the change up on a re-fetch (#1448). emitWorkflow and reportingForward
 * stamp the same value onto their `activeTenant`.
 *
 * Shared by MarkRecord (Physical Inventory Count) and MultiPickerTransactionGrid (Mass Update) so
 * the two copies can't diverge.
 * @param {*} sessionContext
 * @returns {string | undefined}
 */
export function resolveTenantTitleForContinuousScan(sessionContext) {
    return (
        sessionContext?.tenant?.['identity:tenant']?.title ??
        sessionContext?.tenant?.title ??
        sessionContext?.profile?.tenant?.[0]?.['identity:tenant']?.title ??
        sessionContext?.profile?.tenant?.[0]?.title
    );
}

/**
 * Resolve the tenant-authored label for the identifier column ('Item ID', 'Asset ID', 'RMA No'...)
 * so continuous scan's Screen D can show the same header the grid the user just came from shows.
 *
 * Continuous scan hands off via `history.push('/continuous-scan?...')`, and ContinuousScanPage
 * rebuilds its props from the query string alone - the hNode does not survive that hop, so the
 * label has to be resolved here, on the near side, and carried across as a plain string.
 *
 * The label lives on the listColumn hNode, not in metadata services: getTitleAlternativePretty
 * reads a static hardcoded map (lib_ui-services/src/metadata/index.js), so it answers 'Asset ID'
 * for item:item no matter what the tenant authored. Blockly-configured grids
 * (MultiPickerTransactionGrid) carry the real label on the matching child; MarkRecord builds its
 * columns from the static map itself and has no such child, so it falls through to the same
 * 'Asset ID' it already displays - the fallback is what keeps Physical Inventory Count unchanged.
 * @param {object} [hNode] - the grid/action hNode whose `children` hold the listColumn definitions
 * @param {string} [namespace] - the namespace the rows belong to (e.g. 'item')
 * @param {string} [relation] - the relation the rows belong to (e.g. 'item')
 * @returns {string}
 */
export function getIdColumnLabel(hNode, namespace, relation) {
    const propertyName = metadata.getTitleAlternative(namespace, relation, 'assetNo');
    const column = hNode?.children?.find(
        c => c.propertyName === propertyName && !c.foreignNamespace && !c.foreignRelation
    );
    return column?.title || metadata.getTitleAlternativePretty(namespace, relation, 'Asset ID');
}

/**
 * Look at the configured filters on the viewCriteria, and IF there is one for the given
 * namespace:relation, use that to return an _id. If not, return nothing.
 *
 * A find's clause may be a raw id, `{ $eq: id }`, or a single-element `{ $in: [id] }`, and may sit
 * directly on a find's `val` or nested inside `$and`/`$or` -- see the unit tests for the concrete
 * shapes this covers.
 * @param {*} viewCriteria
 * @param {string} namespace='location'
 * @param {string} relation='location'
 * @returns {string | undefined}
 */
export function getRequiredSelectionId(viewCriteria, namespace = 'location', relation = 'location') {
    if (viewCriteria == null) return;

    const key = `${namespace}:${relation}._id`;
    const filters = viewCriteria.getFinds();
    for (const f of filters) {
        const id = getIdFromClause(f.val, key);
        if (id != null) return id;
    }
}

function getIdFromClause(clause, key) {
    if (clause == null) return;
    const value = clause[key];
    if (value != null) {
        if (typeof value !== 'object') return value; // raw id value
        if (Array.isArray(value.$in)) {
            // A multi-select (e.g. MultiPicker) filter's $in only names a single selection
            // unambiguously when it has exactly one id -- otherwise there's no one id to return.
            return value.$in.length === 1 ? value.$in[0] : undefined;
        }
        return value.$eq;
    }
    if (Array.isArray(clause.$and)) {
        for (const andClause of clause.$and) {
            const id = getIdFromClause(andClause, key);
            if (id != null) return id;
        }
    }
    if (Array.isArray(clause.$or)) {
        for (const orClause of clause.$or) {
            const id = getIdFromClause(orClause, key);
            if (id != null) return id;
        }
    }
}
